// Proof of a completed request (SIGN-FE-01): the evidence certificate (a PDF the worker builds a few
// seconds after the last signature; the page asks again while it is "pending") and "Vérifier
// l'intégrité", which checks the stored file and the evidence record against their hashes now. The
// result is words and icons, never a colour alone. The 60-second link of the certificate is asked at
// the click and handed to the browser; it is never shown, stored or logged.
import { SectionCard, StatusChip, isolate } from '@batiflow/ui';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Spinner,
  __css,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowDownload20Regular,
  CheckmarkCircle16Regular,
  DismissCircle16Regular,
  QuestionCircle16Regular,
  ShieldCheckmark20Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import { saveFromLink, useEvidenceLink, useVerifySignature } from './api';
import { useWhen } from './bits';
import { errorMessage, verifyLines, type SignatureRequest, type VerifyLine } from './model';

const useStyles = __css({"body":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"text":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb"},"actions":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f1wn3y0l"},"result":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"resultTitle":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Be2twd7":"fkhj508","Bhrd7zp":"fl43uef"},"lines":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fgdu4nw","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"line":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f778t4r"},"lineText":{"Btd35i7":"fjksvth","Bgto7am":"fuzgk9b","xawz":"fb2jqpd","Bh6795r":"fqerorx"}});

const LINE_INTENT = { ok: 'success', ko: 'danger', unknown: 'neutral' } as const;
const LINE_ICON = {
  ok: <CheckmarkCircle16Regular />,
  ko: <DismissCircle16Regular />,
  unknown: <QuestionCircle16Regular />,
};

export interface EvidenceSectionProps {
  orgId: string;
  request: SignatureRequest;
  /** The certificate is slow: the page stopped asking by itself. */
  waitedTooLong: boolean;
  onRestartPolling: () => void;
}

export function EvidenceSection({
  orgId,
  request,
  waitedTooLong,
  onRestartPolling,
}: EvidenceSectionProps) {
  const { t } = useTranslation('signatures');
  const styles = useStyles();
  const notify = useNotify();
  const { moment } = useWhen();
  const link = useEvidenceLink(orgId);
  const verify = useVerifySignature(orgId);
  const status = request.evidence.status;

  const download = async () => {
    try {
      saveFromLink(await link.mutateAsync(request.id));
      notify({ intent: 'success', message: t('evidence.started') });
    } catch (failure) {
      notify({ intent: 'error', message: errorMessage(t, failure) });
    }
  };

  const nameOf = (signerId: string | undefined) =>
    request.signers.find((signer) => signer.id === signerId)?.user.name ?? '';

  const lineText = (line: VerifyLine): string =>
    line.id === 'signature'
      ? t(`verify.signature.${line.state}`, { name: isolate(nameOf(line.signerId)) })
      : t(`verify.${line.id}.${line.state}`);

  return (
    <SectionCard title={t('evidence.title')} headingLevel={2}>
      <div className={styles.body}>
        <p className={styles.text}>{t('evidence.intro')}</p>

        {status === 'pending' ? (
          waitedTooLong ? (
            <MessageBar intent="warning" layout="multiline">
              <MessageBarBody>{t('evidence.slow')}</MessageBarBody>
              <MessageBarActions>
                <Button onClick={onRestartPolling}>{t('evidence.refresh')}</Button>
              </MessageBarActions>
            </MessageBar>
          ) : (
            <Spinner size="tiny" label={t('evidence.preparing')} labelPosition="after" />
          )
        ) : null}
        {status === 'failed' ? (
          <MessageBar intent="error" layout="multiline">
            <MessageBarBody>{t('evidence.failed')}</MessageBarBody>
          </MessageBar>
        ) : null}

        <div className={styles.actions}>
          {status === 'ready' || status === 'pending' ? (
            <Button
              appearance="primary"
              icon={<ArrowDownload20Regular />}
              disabledFocusable={status !== 'ready' || link.isPending}
              onClick={() => {
                void download();
              }}
            >
              {t('evidence.download')}
            </Button>
          ) : null}
          <Button
            appearance="secondary"
            icon={verify.isPending ? <Spinner size="tiny" /> : <ShieldCheckmark20Regular />}
            disabledFocusable={verify.isPending}
            onClick={() => {
              verify.mutate(request.id);
            }}
          >
            {verify.isPending ? t('verify.running') : t('verify.action')}
          </Button>
        </div>

        {verify.isError ? (
          <MessageBar intent="error" layout="multiline" role="alert">
            <MessageBarBody>{errorMessage(t, verify.error)}</MessageBarBody>
          </MessageBar>
        ) : null}
        {verify.data ? (
          <section className={styles.result} aria-label={t('verify.title')}>
            <h3 className={styles.resultTitle}>{t('verify.title')}</h3>
            <ul className={styles.lines}>
              {verifyLines(verify.data).map((line, index) => (
                <li key={`${line.id}-${String(index)}`} className={styles.line}>
                  <StatusChip
                    intent={LINE_INTENT[line.state]}
                    icon={LINE_ICON[line.state]}
                    label={t(`verify.states.${line.state}`)}
                  />
                  <span className={styles.lineText}>{lineText(line)}</span>
                </li>
              ))}
            </ul>
            <p className={styles.text}>
              {t('verify.checkedAt', { date: moment(verify.data.checkedAt) })}
            </p>
          </section>
        ) : null}
      </div>
    </SectionCard>
  );
}


import "/opt/batiflow/app/apps/web/src/features/signatures/evidence-section.griffel.css";